import { number, object } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useDroppable } from "@dnd-kit/react";
import { format } from "date-fns";
import { getExpansionDateKey, getDateKey } from "@repo/structure";
import { useEstimateEditorSettings, useEstimateStructure, useEstimateTableSettings } from "@tools";
import {
  formatMonth,
  timelineCollisionDetection,
} from "@tools/EstimateEditor/utils/index";
import { TooltipIcon } from "@components";
import { TimelineExpandingWeekHeader } from "./TimelineExpandingWeekHeader";
import { TimelineExpandingWeek } from "./TimelineExpandingWeek";
import { ExpandMore } from "@material-ui/icons";
import useStyle from "./TimelineExpandingComponent.style";
import classnames from "classnames";

export const TimelineExpandingMonth = observer(({ month, monthIndex }) => {
  const { t, i18n } = useTranslation();
  const { allowEdition, blockExpansions, isPdfPrinterView } =
    useEstimateEditorSettings();
  const structure = useEstimateStructure();
  const { timelineMonths } = useEstimateTableSettings();
  const classes = useStyle();

  const language = i18n.resolvedLanguage || i18n.language || "en";
  const monthExpansions = structure.settings.timelineExpandedMonths?.get(
    getExpansionDateKey(month.startDate, true),
  );

  const isExpanded = Boolean(monthExpansions);
  const nextMonth = timelineMonths[monthIndex + 1];
  const nextMonthExpanded = Boolean(
    nextMonth && structure.settings.timelineExpandedMonths?.get(
      getExpansionDateKey(nextMonth.startDate, true),
    ),
  );
  const alternate = Boolean(monthIndex % 2);

  const { ref, isDropTarget } = useDroppable({
    id: `month-${month.startDate.getTime()}`,
    type: "month",
    data: {
      date: month.startDate,
      periods: month.weeks.length,
    },
    disabled: !allowEdition || isExpanded,
    collisionDetector: timelineCollisionDetection,
  });

  const toggleExpanded = () => {
    if (blockExpansions) return;
    structure.settings.setTimelineMonthExpanded(month.startDate, !isExpanded);
  };

  return (
    <div
      className={classnames(
        classes.month,
        isExpanded && classes.activeMonth,
        isExpanded && "preset-active-1",
        alternate && classes.alternate,
        !isExpanded && classes.collapsed,
      )}
    >
      <div className={classnames(classes.monthHeader, "timelineGridMonthHeader")}>
        <div className={classnames(classes.label, "transparent-3")}>
          {
            (monthIndex === 0 || month.startDate.getMonth() === 0) &&
            format(month.startDate, "yyyy")
          }
        </div>
        <div
          className={classnames(
            classes.label,
            !isExpanded && classes.collapsed,
          )}
        >
          {formatMonth(
            month.startDate,
            language,
            isExpanded && month.weeks.length >= 2,
          )}
          {!isPdfPrinterView && (
            <TooltipIcon
              icon={
                <ExpandMore
                  className={classnames(
                    "expand transition -rotate-90",
                    isExpanded && "rotate-90",
                  )}
                />
              }
              onClick={toggleExpanded}
              className={classnames(
                "noDrag",
                classes.expandButton,
                isExpanded ? classes.expandButtonExpanded : "monthHoverIcon",
              )}
              color="primary"
            >
              {t(
                `views.editor.timeline_table.${
                  isExpanded ? "hide" : "show"
                }_week`,
              )}
            </TooltipIcon>
          )}
        </div>
        {isExpanded && (
          <div className="flex h-full">
            {month.weeks.map((week) => (
              <TimelineExpandingWeekHeader
                key={getDateKey(week.startDate)}
                month={month}
                monthIndex={monthIndex}
                expansions={monthExpansions.weeks}
                week={week}
                language={language}
              />
            ))}
          </div>
        )}
      </div>
      <div
        ref={ref}
        className={classnames(
          classes.body,
          classes.monthBody,
          (isExpanded || nextMonthExpanded) && classes.activeMonthDivider,
          monthIndex === 0 && classes.firstMonthBody,
          isDropTarget && classes.isOver,
        )}
      >
        {isExpanded &&
          month.weeks.map((week, weekIndex) => (
            <TimelineExpandingWeek
              key={getDateKey(week.startDate)}
              expansions={monthExpansions.weeks}
              week={week}
              nextWeekExpanded={Boolean(
                month.weeks[weekIndex + 1] &&
                monthExpansions.weeks.get(
                  getExpansionDateKey(month.weeks[weekIndex + 1].startDate, false),
                )
              )}
            />
          ))}
      </div>
    </div>
  );
});

TimelineExpandingMonth.propTypes = {
  month: object.isRequired,
  monthIndex: number.isRequired,
};

TimelineExpandingMonth.displayName = "TimelineExpandingMonth";
